const DAY_ABBREV = {
  monday: "Mon",
  mon: "Mon",
  tuesday: "Tue",
  tue: "Tue",
  wednesday: "Wed",
  wed: "Wed",
  thursday: "Thu",
  thu: "Thu",
  friday: "Fri",
  fri: "Fri",
  saturday: "Sat",
  sat: "Sat",
  sunday: "Sun",
  sun: "Sun",
  daily: "Daily",
};

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_ORDER = [...WEEKDAYS, "Daily"];

const PRICING_LABELS = {
  drop_in: "Per class",
  pack: "Class pack",
  monthly: "Monthly membership",
};

function padHour(hour) {
  const num = Number(hour);
  return Number.isFinite(num) ? num : 0;
}

function to12Hour(hour, minute = "00") {
  const h = padHour(hour);
  const m = String(minute).padStart(2, "0");
  const period = h >= 12 ? "PM" : "AM";
  const normalized = h % 12 === 0 ? 12 : h % 12;
  return `${normalized}:${m} ${period}`;
}

export function formatTimeValue(time) {
  if (!time) return "";
  const raw = String(time).trim();
  const rangeMatch = raw.match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
  if (rangeMatch) {
    return `${to12Hour(rangeMatch[1], rangeMatch[2])} – ${to12Hour(rangeMatch[3], rangeMatch[4])}`;
  }
  const singleMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (singleMatch) {
    return to12Hour(singleMatch[1], singleMatch[2]);
  }
  return raw;
}

export function normalizeDayToken(token) {
  const key = String(token || "").trim().toLowerCase().replace(/\.$/, "");
  return DAY_ABBREV[key] || String(token || "").trim();
}

export function formatDayList(days) {
  if (!Array.isArray(days) || days.length === 0) return "";
  const normalized = days.map(normalizeDayToken).filter(Boolean);
  const unique = [...new Set(normalized)];

  if (unique.includes("Daily") || WEEKDAYS.every((day) => unique.includes(day))) {
    return "Daily";
  }

  unique.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  return unique.join(", ");
}

export function pricingModelLabel(pricingModel, legacyLabel = "") {
  const model = String(pricingModel || "").trim().toLowerCase();
  if (PRICING_LABELS[model]) return PRICING_LABELS[model];

  const label = String(legacyLabel || "").trim().toLowerCase();
  if (!label) return "";
  if (label.includes("monthly")) return PRICING_LABELS.monthly;
  if (label.includes("pack")) return PRICING_LABELS.pack;
  if (label.includes("drop")) return PRICING_LABELS.drop_in;
  // Free-text schedule labels from scrapers are not pricing models.
  return "";
}

export function parseScheduleJson(scheduleJson) {
  if (!scheduleJson) return [];
  if (Array.isArray(scheduleJson)) return scheduleJson;
  if (typeof scheduleJson === "string") {
    try {
      const parsed = JSON.parse(scheduleJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function looksLikeClockTime(value) {
  const raw = String(value || "").trim();
  return /^\d{1,2}:\d{2}(\s*-\s*\d{1,2}:\d{2})?$/.test(raw);
}

export function formatScheduleEntry(entry) {
  if (!entry) return { days: "", time: "", timezone: "", pricing: "", label: "" };

  const days = formatDayList(entry.days);
  const rawTime = String(entry.time || "").trim();
  const time = looksLikeClockTime(rawTime) ? formatTimeValue(rawTime) : "";
  const timezone = entry.timezone ? String(entry.timezone).trim() : days || time ? "IST" : "";
  const pricing = pricingModelLabel(entry.pricing_model, entry.label);

  // Legacy scraper entries: free-text label only, no structured days/time.
  if (!days && !time) {
    const legacy = String(entry.label || entry.time || "").trim();
    return {
      days: "",
      time: legacy,
      timezone: "",
      pricing: "",
      label: legacy,
    };
  }

  const parts = [
    days,
    [time, timezone].filter(Boolean).join(" "),
    pricing,
  ].filter(Boolean);

  return {
    days,
    time,
    timezone,
    pricing,
    label: parts.join(" · "),
  };
}

export function formatScheduleCompact(scheduleJsonOrSummary, fallbackSummary = "") {
  const schedule = parseScheduleJson(scheduleJsonOrSummary);
  if (schedule.length > 0) {
    const formatted = formatScheduleEntry(schedule[0]);
    if (formatted.label) return formatted.label;
    return fallbackSummary || "";
  }

  if (typeof scheduleJsonOrSummary === "string" && scheduleJsonOrSummary.trim()) {
    return scheduleJsonOrSummary.trim();
  }

  return fallbackSummary || "";
}

export function formatScheduleLines(scheduleJson) {
  return parseScheduleJson(scheduleJson).map((entry) => formatScheduleEntry(entry));
}
